import { useState } from "react";
import { api } from "@/lib/api";
import { useReveal } from "@/hooks/useReveal";
import { toast } from "sonner";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Newsletter() {
  const ref = useReveal();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [recipientAge, setRecipientAge] = useState("");
  const [postalAddress, setPostalAddress] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const emailValid = EMAIL_RE.test(email.trim());

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!emailValid) {
      toast.error("Please enter a valid email");
      return;
    }
    if (!recipientName.trim()) {
      toast.error("Please tell us who the box is for");
      return;
    }
    if (!recipientAge) {
      toast.error("Please choose the recipient's age");
      return;
    }
    if (!postalAddress.trim()) {
      toast.error("Please add a postal address");
      return;
    }
    try {
      setSubmitting(true);
      const res = await api.post("/newsletter/subscribe", {
        email: email.trim(),
        name: name.trim() || null,
        recipient_name: recipientName.trim(),
        recipient_age: Number(recipientAge),
        postal_address: postalAddress.trim(),
      });
      if (res.data?.success) {
        setDone(true);
        toast.success(res.data.message || "You're in.");
      }
    } catch (e) {
      console.error(e);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="newsletter" className="py-24 sm:py-32 bg-[#FFFDF9]">
      <div
        ref={ref}
        className="reveal max-w-4xl mx-auto px-6 sm:px-10 text-center"
        data-testid="newsletter-section"
      >
        <span className="text-[11px] tracking-[0.3em] uppercase text-[#C48B9F]">
          The Pen Pal Club
        </span>
        <h2 className="mt-4 font-serif-me text-4xl sm:text-5xl leading-[1.05] text-[#36292A]">
          Join our little{" "}
          <em className="italic text-[#C48B9F] font-normal">circle of love.</em>
        </h2>
        <p className="mt-5 text-[#7D6B6D] max-w-xl mx-auto">
          Be first to hear when new seasonal boxes drop, get invitations to
          salon events, and receive a hand-written welcome letter from the Me
          Amor founder.
        </p>

        {!done ? (
          <form
            onSubmit={onSubmit}
            className="mt-12 max-w-xl mx-auto grid grid-cols-1 sm:grid-cols-6 gap-4 text-left"
            data-testid="newsletter-form"
          >
            <div className="sm:col-span-2">
              <label className="text-[10px] tracking-[0.28em] uppercase text-[#7D6B6D]">
                Your name (optional)
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className="me-input"
                data-testid="newsletter-name-input"
              />
            </div>
            <div className="sm:col-span-4">
              <label className="text-[10px] tracking-[0.28em] uppercase text-[#7D6B6D]">
                Email
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@meamor.com"
                className="me-input"
                data-testid="newsletter-email-input"
              />
            </div>

            {/* Extra fields — appear once a valid email is entered */}
            <div
              className={
                "sm:col-span-6 grid grid-cols-1 sm:grid-cols-6 gap-4 overflow-hidden transition-all duration-500 " +
                (emailValid
                  ? "opacity-100 max-h-[900px] mt-2"
                  : "opacity-0 max-h-0 pointer-events-none")
              }
              aria-hidden={!emailValid}
              data-testid="newsletter-extra-fields"
            >
              <div className="sm:col-span-6">
                <div className="text-[10px] tracking-[0.28em] uppercase text-[#C48B9F]">
                  A little about her
                </div>
                <div className="mt-1 text-sm text-[#7D6B6D]">
                  So we can post her the perfect welcome letter and prepare her seasonal boxes.
                </div>
              </div>

              <div className="sm:col-span-3">
                <label className="text-[10px] tracking-[0.28em] uppercase text-[#7D6B6D]">
                  Recipient&apos;s name
                </label>
                <input
                  type="text"
                  value={recipientName}
                  onChange={(e) => setRecipientName(e.target.value)}
                  placeholder="Her first name"
                  className="me-input"
                  data-testid="newsletter-recipient-name"
                />
              </div>
              <div className="sm:col-span-3">
                <label className="text-[10px] tracking-[0.28em] uppercase text-[#7D6B6D]">
                  Recipient&apos;s age
                </label>
                <select
                  value={recipientAge}
                  onChange={(e) => setRecipientAge(e.target.value)}
                  className="me-input bg-transparent"
                  data-testid="newsletter-recipient-age"
                >
                  <option value="">Choose age…</option>
                  {Array.from({ length: 12 }, (_, i) => i + 4).map((age) => (
                    <option key={age} value={age}>
                      {age} years old
                    </option>
                  ))}
                </select>
              </div>

              <div className="sm:col-span-6">
                <label className="text-[10px] tracking-[0.28em] uppercase text-[#7D6B6D]">
                  Postal address (New Zealand)
                </label>
                <textarea
                  value={postalAddress}
                  onChange={(e) => setPostalAddress(e.target.value)}
                  placeholder={"12 Rose Street\nPonsonby\nAuckland 1011"}
                  rows={3}
                  className="mt-1 w-full rounded-lg border border-[#E8D7D0] bg-white px-3 py-2 text-sm text-[#36292A] focus:outline-none focus:border-[#C48B9F] resize-none"
                  data-testid="newsletter-postal-address"
                />
              </div>
            </div>

            <div className="sm:col-span-6 flex justify-center mt-4">
              <button
                type="submit"
                className="me-btn-primary"
                disabled={submitting}
                data-testid="newsletter-submit"
              >
                {submitting ? "Sending…" : "Join the club"}
              </button>
            </div>
          </form>
        ) : (
          <div
            className="mt-12 max-w-md mx-auto border border-[#E8D7D0] rounded-2xl p-8 bg-[#F9F6F0]"
            data-testid="newsletter-thankyou"
          >
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#C48B9F]">
              Welcome
            </div>
            <div className="mt-3 font-serif-me text-2xl text-[#36292A]">
              You&apos;re on the list.
            </div>
            <p className="mt-3 text-sm text-[#7D6B6D]">
              Keep an eye on your inbox. Something beautiful is coming your
              way.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
